import { cn } from "@/utils";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

export type SortDir = "asc" | "desc";

interface SortHeaderButtonProps<TField extends string> {
  field: TField;
  label: string;
  activeField: TField;
  sortDir: SortDir;
  onSortChange: (field: TField) => void;
  className?: string;
}

/**
 * Column-header label that doubles as a sort toggle — the workspace detail
 * list view's header treatment, now hosted by the browser list's flex-based
 * column header (it used to live inside a `TableHead`).
 *
 * Click cycles ascending ↔ descending for the active field; switching fields
 * lets the caller snap to each field's natural default direction. Generic
 * over the caller's sort-field union (e.g. `"name" | "modified" | "size"`)
 * so usage stays type-safe per list.
 */
export function SortHeaderButton<TField extends string>({
  field,
  label,
  activeField,
  sortDir,
  onSortChange,
  className,
}: SortHeaderButtonProps<TField>) {
  const isActive = activeField === field;
  const Icon = !isActive
    ? ArrowUpDown
    : sortDir === "asc"
      ? ArrowUp
      : ArrowDown;

  return (
    <button
      type="button"
      onClick={() => onSortChange(field)}
      className={cn(
        "-ml-1.5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-1.5 py-1 text-xs font-medium text-muted-foreground transition-colors",
        "hover:bg-muted hover:text-foreground",
        isActive && "text-foreground",
        className,
      )}
    >
      {label}
      <Icon
        className={cn(
          "h-3 w-3",
          isActive ? "text-foreground" : "text-muted-foreground/50",
        )}
      />
    </button>
  );
}
